import * as THREE from "three";
import {
  GLTFLoader,
  type GLTF,
} from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";

// Reuse loaders to avoid creating new instances each time
let gltfLoader: GLTFLoader | null = null;
let dracoLoader: DRACOLoader | null = null;

function getGLTFLoader(): GLTFLoader {
  if (!gltfLoader) {
    gltfLoader = new GLTFLoader();

    // Set up DRACO loader for compressed models (reuse instance)
    if (!dracoLoader) {
      dracoLoader = new DRACOLoader();
      // Use CDN for DRACO decoders
      dracoLoader.setDecoderPath(
        "https://www.gstatic.com/draco/versioned/decoders/1.5.7/",
      );
    }
    gltfLoader.setDRACOLoader(dracoLoader);
    gltfLoader.setMeshoptDecoder(MeshoptDecoder);
  }
  return gltfLoader;
}

/**
 * Collects every material authored in the GLB onto the scene's userData, both
 * keyed by name (`builtInMaterials`, used by `switchMaterials`) and indexed by
 * glTF material index (`variantMaterials`, used by `applyVariant`). Materials
 * that are only referenced through the `KHR_materials_variants` extension are
 * not bound to any mesh by the loader, so they are resolved explicitly here.
 *
 * The list of variant names declared by `KHR_materials_variants` is stored on
 * `materialVariants` so `applyVariant` can map a variant name to the index used
 * by each primitive's mapping table.
 */
async function attachBuiltInMaterials(gltf: GLTF): Promise<void> {
  const parser = gltf.parser;
  const json = parser.json as {
    materials?: Array<{ name?: string }>;
    extensions?: {
      KHR_materials_variants?: { variants?: Array<{ name?: string }> };
    };
  };
  const materialDefs = json.materials;
  if (!materialDefs?.length) return;

  const materialsByIndex = await Promise.all(
    materialDefs.map(
      (_, index) =>
        parser.getDependency("material", index) as Promise<THREE.Material>,
    ),
  );

  const builtInMaterials: Record<string, THREE.Material> = {};
  materialDefs.forEach((def, index) => {
    if (def.name) builtInMaterials[def.name] = materialsByIndex[index];
  });

  gltf.scene.userData.builtInMaterials = builtInMaterials;
  gltf.scene.userData.variantMaterials = materialsByIndex;
  gltf.scene.userData.materialVariants = (
    json.extensions?.KHR_materials_variants?.variants ?? []
  )
    .map((v) => v.name)
    .filter((name): name is string => Boolean(name));
}

export function loadGLBModel(url: string): Promise<THREE.Group> {
  const loader = getGLTFLoader();

  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (gltf) => {
        attachBuiltInMaterials(gltf)
          .catch((error) => {
            console.error("Failed to collect built-in GLB materials:", error);
          })
          .finally(() => {
            resolve(gltf.scene);
          });
      },
      () => {
        // Progress callback - can be used for loading indicators if needed
      },
      (error) => {
        console.error("GLTFLoader error details:", error);
        console.error("Failed URL:", url);
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        reject(new Error(`Failed to load model from ${url}: ${errorMessage}`));
      },
    );
  });
}
